// workflowDetailsSplit — splittrar full WorkflowState i en lean
// "structure"-del + en lista entity_details. Inversen mergeFromStorage
// kombinerar dem tillbaka vid load.
//
// Lager C (JSON-refactor): workflows.content i DB innehåller bara struktur
// (positions, IDs, links, names, colors). Descriptions, icons, subject,
// to-fält flyttas till public.entity_details.metadata + description_md.
// Attachments redan i entity-attachments-bucket (Fas 7).
//
// Storage-shape per entity:
//   entity_details.description_md  ← block.config.detailedDescription
//                                    (lång markdown från Detaljer-dialogen)
//   entity_details.metadata.icon   ← block.config.icon
//   entity_details.metadata.subject ← block.config.subject
//   entity_details.metadata.to     ← block.config.to
//   entity_details.metadata.label  ← connector.label
//   entity_details.metadata.waypoints ← connector.waypoints
//
// `block.config.description` (kort sammanfattning på block-kortet) bevaras
// i lean-strukturen och persisteras direkt i workflows.content. Den är
// kort och billig att lagra inline; det är `detailedDescription` (med
// markdown + bilagor) som flyttas ut till entity_details.
//
// Vid load: fetch_entity_details_for_workflow(workflow_id) → bulk-merge
// tillbaka i blocks/connectors/groups innan render.

import type {
  WorkflowState,
  MultiSheetWorkflowState,
  WorkflowBlock,
  Connector,
  BlockGroup,
} from '@/types/workflow';
import { isMultiSheetState, wrapSingleSheet, extractSheetState } from '@/types/workflow';

export interface SplitDetail {
  entity_id: string;
  entity_type: 'block' | 'group' | 'connector';
  description_md: string;
  metadata: Record<string, unknown>;
}

/** Detail whose description_md exceeds the 1 MB inline cap and should be
 *  uploaded to the entity-attachments bucket as `block-<id>-detail.md`
 *  instead of stored in the `entity_details.description_md` TEXT column. */
export interface SplitDetailAttachment {
  entity_id: string;
  entity_type: 'block' | 'group' | 'connector';
  /** Suggested filename for the attachment row. */
  filename: string;
  /** MIME type for the upload. Always `text/markdown` today. */
  mime_type: string;
  /** Raw markdown body the caller uploads to storage. */
  body: string;
  size_bytes: number;
  metadata: Record<string, unknown>;
}

/** description_md bytes above which a detail is routed to bucket
 *  storage (`entity_attachments`) instead of the inline TEXT column.
 *  Mirrors the existing 10 MB hard cap on `entity_attachments.size_bytes`
 *  (`schemaSQL.ts:2250`), but kicks in earlier so the TEXT column never
 *  bloats past a megabyte. */
export const MAX_INLINE_DETAIL_BYTES = 1_048_576;

export interface SplitResult {
  /** Lean structure som lagras i workflows.content. */
  lean: MultiSheetWorkflowState;
  /** Per-entity details som lagras i entity_details-tabellen (TEXT). */
  details: SplitDetail[];
  /** Per-entity details vars markdown är för stora för TEXT-kolumnen
   *  och måste skrivas till entity-attachments-bucket istället. */
  attachments: SplitDetailAttachment[];
}

function stripBlock(b: WorkflowBlock): { lean: WorkflowBlock; detail: SplitDetail | null } {
  // `description` (kort summering) stannar i lean. `detailedDescription`
  // (lång markdown) flyttas till entity_details.description_md.
  const { detailedDescription, icon, subject, to, ...keepConfig } = b.config ?? { name: '' };
  const metadata: Record<string, unknown> = {};
  if (icon) metadata.icon = icon;
  if (subject) metadata.subject = subject;
  if (to) metadata.to = to;
  // portSchemas är typing-data, inte text-content — bevaras på block.

  const hasDetails = (detailedDescription && detailedDescription.trim().length > 0)
    || Object.keys(metadata).length > 0;

  const lean: WorkflowBlock = { ...b, config: keepConfig as WorkflowBlock['config'] };
  return {
    lean,
    detail: hasDetails
      ? {
          entity_id: b.id,
          entity_type: 'block',
          description_md: detailedDescription ?? '',
          metadata,
        }
      : null,
  };
}

function stripConnector(c: Connector): { lean: Connector; detail: SplitDetail | null } {
  const { label, waypoints: _waypoints, ...rest } = c;
  void _waypoints;
  const metadata: Record<string, unknown> = {};
  if (label) metadata.label = label;
  // Manuella waypoints persisteras INTE till entity_details — de är
  // session-lokala. Auto-router togs bort i 5c049f1; persisterade
  // waypoints var det sista som kunde tyst återinföra wavy-bug:en
  // efter en reload från moln.

  const hasDetails = Object.keys(metadata).length > 0;
  return {
    lean: rest,
    detail: hasDetails
      ? {
          entity_id: c.id,
          entity_type: 'connector',
          description_md: '',
          metadata,
        }
      : null,
  };
}

function stripGroup(g: BlockGroup): { lean: BlockGroup; detail: SplitDetail | null } {
  // Groups bär redan minimal text-content (name, color); inget att flytta
  // utöver det. Returnera oförändrad.
  return { lean: g, detail: null };
}

/**
 * Splittra workflow-state till lean + details. Lean kan persist:as till
 * workflows.content och details batch-upsertas till entity_details via
 * upsert_entity_details-RPC:n. Multi-sheet bevarar sheet-grupperingen;
 * single-sheet auto-wrappas till multi.
 */
export function splitForStorage(
  state: WorkflowState | MultiSheetWorkflowState,
): SplitResult {
  const multi = isMultiSheetState(state) ? state : wrapSingleSheet(state);
  const details: SplitDetail[] = [];
  const attachments: SplitDetailAttachment[] = [];

  const leanSheets = multi.sheets.map((sheet) => {
    const leanBlocks: WorkflowBlock[] = [];
    for (const b of sheet.blocks) {
      const { lean, detail } = stripBlock(b);
      leanBlocks.push(lean);
      if (detail) routeDetail(detail, details, attachments);
    }
    const leanConnectors: Connector[] = [];
    for (const c of sheet.connectors) {
      const { lean, detail } = stripConnector(c);
      leanConnectors.push(lean);
      if (detail) routeDetail(detail, details, attachments);
    }
    const leanGroups: BlockGroup[] = [];
    for (const g of sheet.groups) {
      const { lean, detail } = stripGroup(g);
      leanGroups.push(lean);
      if (detail) routeDetail(detail, details, attachments);
    }
    return { ...sheet, blocks: leanBlocks, connectors: leanConnectors, groups: leanGroups };
  });

  const lean: MultiSheetWorkflowState = {
    ...multi,
    sheets: leanSheets,
  };

  return { lean, details, attachments };
}

/** Route a single `SplitDetail` into either the inline TEXT-column list
 *  (`details`) or the bucket-upload list (`attachments`) based on
 *  `description_md` byte length. Metadata always rides with the
 *  `entity_details` row — only the long markdown body moves to
 *  the bucket. The TEXT-column row in that case keeps an empty
 *  `description_md` plus a pointer flag in metadata so the loader
 *  knows to fetch the attachment lazily. */
function routeDetail(
  detail: SplitDetail,
  details: SplitDetail[],
  attachments: SplitDetailAttachment[],
): void {
  const md = detail.description_md ?? '';
  const sizeBytes = byteLengthOf(md);
  if (sizeBytes <= MAX_INLINE_DETAIL_BYTES) {
    details.push(detail);
    return;
  }
  // Long body → bucket; entity_details row gets a deferred-pointer
  // metadata flag so the loader knows to hydrate from storage.
  attachments.push({
    entity_id: detail.entity_id,
    entity_type: detail.entity_type,
    filename: `${detail.entity_type}-${detail.entity_id}-detail.md`,
    mime_type: 'text/markdown',
    body: md,
    size_bytes: sizeBytes,
    metadata: detail.metadata,
  });
  details.push({
    entity_id: detail.entity_id,
    entity_type: detail.entity_type,
    description_md: '',
    metadata: {
      ...detail.metadata,
      _detail_in_bucket: true,
      _detail_bytes: sizeBytes,
    },
  });
}

function byteLengthOf(s: string): number {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(s).length;
  }
  // Fallback for envs that lack TextEncoder — overestimates non-ASCII
  // but never underestimates, so the threshold gate stays safe.
  return s.length;
}

/**
 * Slå ihop lean + details tillbaka till körnings-state. Idempotent: om
 * details är tom array returneras lean oförändrad. Multi-sheet bevarar
 * shape:n; om input är single-sheet WorkflowState returneras single-sheet.
 */
export function mergeFromStorage(
  lean: WorkflowState | MultiSheetWorkflowState,
  details: SplitDetail[],
): WorkflowState | MultiSheetWorkflowState {
  if (details.length === 0) return lean;

  const byId = new Map<string, SplitDetail>(details.map((d) => [d.entity_id, d]));
  const wasSingle = !isMultiSheetState(lean);
  const multi = wasSingle ? wrapSingleSheet(lean as WorkflowState) : (lean as MultiSheetWorkflowState);

  const mergedSheets = multi.sheets.map((sheet) => ({
    ...sheet,
    blocks: sheet.blocks.map((b) => {
      const det = byId.get(b.id);
      if (!det || det.entity_type !== 'block') return b;
      const meta = det.metadata ?? {};
      return {
        ...b,
        config: {
          ...b.config,
          ...(det.description_md ? { detailedDescription: det.description_md } : {}),
          ...(typeof meta.icon === 'string' ? { icon: meta.icon } : {}),
          ...(typeof meta.subject === 'string' ? { subject: meta.subject } : {}),
          ...(typeof meta.to === 'string' ? { to: meta.to } : {}),
        },
      };
    }),
    connectors: sheet.connectors.map((c) => {
      const det = byId.get(c.id);
      if (!det || det.entity_type !== 'connector') return c;
      const meta = det.metadata ?? {};
      // meta.waypoints (legacy stored data) ignoreras — persistens
      // av waypoints är avstängd, manuella är session-lokala.
      return {
        ...c,
        ...(typeof meta.label === 'string' ? { label: meta.label } : {}),
      };
    }),
  }));

  const mergedMulti: MultiSheetWorkflowState = { ...multi, sheets: mergedSheets };
  if (wasSingle) {
    return extractSheetState(mergedMulti, mergedMulti.activeSheetIndex);
  }
  return mergedMulti;
}
